<template>
  <div class="multiselect-wrapper" :class="{ 'chips-below': showSelectedBelow }">
    <!-- MultiSelect Principal (input para digitar e filtrar) -->
    <VueMultiselect 
      v-model="selectedItems"
      :options="processedOptions"
      :selectedLabel="'selecionado'" 
      :searchable="searchable" 
      :placeholder="placeholder"
      :selectLabel="null"
      :deselectLabel="null" 
      :showPointer="false" 
      :closeOnSelect="false" 
      :multiple="true" 
      :label="'name'" 
      :track-by="'id'"
      :disabled="disabled"
      class="multiselect-custom"
      @input="onSelectionChange"
    >
      <!-- Slot customizado para as opções do dropdown -->
      <template #option="{ option }">
        <div class="custom-option">
          <span>{{ option.displayName || option.name }}</span>
        </div>
      </template>

      <!-- Slot para quando não há opções -->
      <template #noOptions>
        <span class="no-options">Nenhuma opção disponível</span>
      </template>

      <!-- Slot para quando não há resultados -->
      <template #noResult>
        <span class="no-result">Nenhum resultado encontrado</span>
      </template>
    </VueMultiselect>

    <!-- Área de badges dos selecionados (abaixo do input) -->
    <div v-if="showSelectedBelow && hasSelectedOptions" class="selected-chips">
      <div class="selected-chips-header">
        <span class="selected-count">{{ selectedItems.length }} selecionado{{ selectedItems.length !== 1 ? 's' : '' }}</span>
      </div>
      <div class="selected-chips-list">
        <button
          v-for="item in selectedItems"
          :key="item.id || item.name"
          type="button"
          class="selected-chip"
          :disabled="disabled"
          @click="removeItem(item)"
        >
          <span class="chip-label">{{ item.displayName || item.name || item.label }}</span>
          <i class="fa-solid fa-xmark chip-remove"></i>
        </button>
      </div>
    </div>

    <!-- Botões de Selecionar/Desmarcar Todos (Abaixo do Select) -->
    <div v-if="showSelectAllButtons" class="select-all-buttons">
      <button 
        type="button" 
        class="select-all-btn select-all"
        @click="selectAll"
        :disabled="disabled || !hasUnselectedOptions"
      >
        <i class="fa-solid fa-check-double"></i>
        <span>Selecionar Todos</span>
      </button>
      <button 
        type="button" 
        class="select-all-btn deselect-all"
        @click="deselectAll"
        :disabled="disabled || !hasSelectedOptions"
      >
        <i class="fa-solid fa-times"></i>
        <span>Desmarcar Todos</span>
      </button>
    </div>
  </div>
</template>

<script>
import VueMultiselect from "vue-multiselect";

export default {
  name: "MultiSelect",
  components: { VueMultiselect },
  props: {
    // Valor selecionado (v-model)
    modelValue: {
      type: Array,
      default: () => []
    },
    // Opções disponíveis
    options: {
      type: Array,
      default: () => []
    },
    // Texto do placeholder
    placeholder: {
      type: String,
      default: 'Selecione as opções'
    },
    // Se deve mostrar os botões de selecionar/desmarcar todos
    showSelectAllButtons: {
      type: Boolean,
      default: false
    },
    // Se o campo é pesquisável (permite digitar para filtrar)
    searchable: {
      type: Boolean,
      default: true
    },
    // Mostrar chips dos selecionados em área dedicada abaixo (melhor visualização)
    showSelectedBelow: {
      type: Boolean,
      default: true
    },
    // Desabilita seleção e botões auxiliares
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  computed: {
    selectedItems: {
      get() {
        return Array.isArray(this.modelValue) ? this.modelValue : [];
      },
      set(value) {
        this.$emit('update:modelValue', Array.isArray(value) ? value : []);
      }
    },
    processedOptions() {
      if (!this.options || !Array.isArray(this.options)) return [];
      
      return this.options.map(option => ({
        ...option,
        // Para as tags selecionadas, usar a label completa (com vinculação)
        // Para o dropdown, manter apenas o nome original da região
        name: option.label || option.name || 'Sem nome',
        displayName: option.name || 'Sem nome' // Nome original para o dropdown
      }));
    },
    hasSelectedOptions() {
      return this.selectedItems && this.selectedItems.length > 0;
    },
    hasUnselectedOptions() {
      if (!this.processedOptions || !Array.isArray(this.processedOptions)) return false;
      if (!this.selectedItems || !Array.isArray(this.selectedItems)) return this.processedOptions.length > 0;
      
      return this.processedOptions.length > this.selectedItems.length;
    }
  },
  methods: {
    selectAll() {
      if (this.disabled) return;
      if (!this.processedOptions || !Array.isArray(this.processedOptions)) return;
      
      // Selecionar todas as opções disponíveis
      this.selectedItems = [...this.processedOptions];
    },
    
    deselectAll() {
      if (this.disabled) return;
      // Desmarcar todas as opções
      this.selectedItems = [];
    },
    
    onSelectionChange(newValue) {
      if (this.disabled) return;
      this.$emit('update:modelValue', newValue);
    },
    removeItem(item) {
      if (this.disabled) return;
      const updated = this.selectedItems.filter(i => (i.id || i) !== (item.id || item));
      this.selectedItems = updated;
    }
  }
};
</script>

<style scoped>
/* Container principal do wrapper */
.multiselect-wrapper {
  position: relative;
  width: 100%;
}

/* Área de chips dos selecionados (abaixo do input) */
.selected-chips {
  margin-top: 0.6rem;
  padding: 0.5rem 0.6rem;
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  transition: all 0.2s ease;
}

[data-theme="dark"] .selected-chips {
  background: var(--bg-tertiary, #1c1c1f);
}

.selected-chips-header {
  margin-bottom: 0.5rem;
}

.selected-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.selected-chips-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.2rem 0.45rem;
  background: linear-gradient(135deg, var(--primary-color, #3b82f6), #2563eb);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.selected-chip:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
}

.selected-chip:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
}

.selected-chip .chip-label {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-chip .chip-remove {
  font-size: 0.6rem;
  opacity: 0.9;
}

.selected-chip:hover .chip-remove {
  opacity: 1;
}

/* Estilo dos botões de Selecionar/Desmarcar Todos */
.select-all-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-top: 1rem;
  width: 100%;
}

.select-all-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  background: var(--card-background);
  color: var(--text-primary);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  min-height: 36px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.select-all-btn:before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.select-all-btn:hover:not(:disabled):before {
  left: 100%;
}

.select-all-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: var(--primary-color);
}

.select-all-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.select-all-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--bg-tertiary);
  color: var(--text-muted);
  transform: none;
  box-shadow: none;
}

.select-all-btn:disabled:before {
  display: none;
}

/* Estilo específico para botão Selecionar Todos */
.select-all-btn.select-all {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border-color: #10b981;
}

.select-all-btn.select-all:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669, #047857);
  border-color: #059669;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.select-all-btn.select-all:disabled {
  background: var(--bg-tertiary);
  color: var(--text-muted);
  border-color: var(--border-color);
}

/* Estilo específico para botão Desmarcar Todos */
.select-all-btn.deselect-all {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border-color: #ef4444;
}

.select-all-btn.deselect-all:hover:not(:disabled) {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  border-color: #dc2626;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.select-all-btn.deselect-all:disabled {
  background: var(--bg-tertiary);
  color: var(--text-muted);
  border-color: var(--border-color);
}

/* Ícones dos botões */
.select-all-btn i {
  font-size: 0.875rem;
  flex-shrink: 0;
}

.select-all-btn span {
  font-weight: 600;
  letter-spacing: 0.025em;
  line-height: 1.2;
}

/* Estilos para slots customizados */
.custom-option {
  display: flex;
  align-items: center;
  width: 100%;
}

.no-options,
.no-result {
  color: var(--text-muted);
  font-style: italic;
  padding: 0.5rem;
  text-align: center;
}

/* Responsividade para botões */
@media (max-width: 768px) {
  .select-all-buttons {
    gap: 0.4rem;
    margin-top: 0.75rem;
  }
  
  .select-all-btn {
    padding: 0.45rem 0.6rem;
    font-size: 0.75rem;
    min-height: 32px;
  }
  
  .select-all-btn i {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .select-all-btn {
    padding: 0.4rem 0.5rem;
    font-size: 0.7rem;
    min-height: 30px;
    gap: 0.375rem;
  }
  
  .select-all-btn i {
    font-size: 0.75rem;
  }
  
  .select-all-btn span {
    font-size: 0.7rem;
  }
}

/* Tema escuro - ajustes específicos */
[data-theme="dark"] .select-all-btn:not(.select-all):not(.deselect-all) {
  background: var(--card-background);
  border-color: var(--border-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

[data-theme="dark"] .select-all-btn:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

/* Estilos personalizados para MultiSelect com suporte a temas */
.multiselect-custom {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  position: relative !important;
  z-index: 1 !important;
}

/* Container principal do multiselect - segue tema dark/light */
.multiselect-custom :deep(.multiselect) {
  min-height: 44px !important;
  background: var(--bg-secondary, #e2e8f0) !important;
  border: 2px solid var(--border-color) !important;
  border-radius: 8px !important;
  color: var(--text-primary) !important;
  font-size: 14px !important;
  transition: var(--transition) !important;
  box-shadow: var(--shadow-sm) !important;
  position: relative !important;
}

.multiselect-custom :deep(.multiselect:focus),
.multiselect-custom :deep(.multiselect--active) {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 3px var(--primary-color-10) !important;
  z-index: 10 !important;
}

/* Tags container - segue tema dark/light */
.multiselect-custom :deep(.multiselect__tags) {
  min-height: 44px !important;
  background: var(--bg-secondary, #e2e8f0) !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 8px 40px 8px 12px !important;
  font-size: 14px !important;
}

/* Input de digitação - segue tema dark/light (sobrescreve estilos padrão da lib) */
.multiselect-custom :deep(.multiselect__input) {
  background: transparent !important;
  color: var(--text-primary) !important;
  caret-color: var(--text-primary) !important;
}

.multiselect-custom :deep(.multiselect__input::placeholder) {
  color: var(--text-muted) !important;
}

/* Quando chips abaixo: esconder tags no input (evitar duplicação) */
.multiselect-wrapper.chips-below .multiselect-custom :deep(.multiselect__tag) {
  display: none !important;
}

/* Tags individuais (dentro do input, quando não chips-below) */
.multiselect-custom :deep(.multiselect__tag) {
  background: var(--primary-color) !important;
  color: white !important;
  border-radius: 6px !important;
  padding: 4px 26px 4px 10px !important;
  margin-right: 8px !important;
  margin-bottom: 4px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  line-height: 1.2 !important;
  border: none !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
}

/* Ícone de remoção da tag */
.multiselect-custom :deep(.multiselect__tag-icon) {
  border-radius: 0 6px 6px 0 !important;
  width: 20px !important;
  line-height: 20px !important;
  background: rgba(255, 255, 255, 0.2) !important;
  transition: var(--transition) !important;
}

.multiselect-custom :deep(.multiselect__tag-icon:after) {
  color: white !important;
  font-size: 12px !important;
  font-weight: 600 !important;
}

.multiselect-custom :deep(.multiselect__tag-icon:hover) {
  background: rgba(255, 255, 255, 0.3) !important;
}

/* Seta do dropdown */
.multiselect-custom :deep(.multiselect__select) {
  height: 42px !important;
  width: 40px !important;
  background: transparent !important;
  border: none !important;
  border-radius: 0 8px 8px 0 !important;
  transition: var(--transition) !important;
}

.multiselect-custom :deep(.multiselect__select:before) {
  border-color: var(--text-secondary) transparent transparent transparent !important;
  border-width: 5px 4px 0 4px !important;
  margin-top: 2px !important;
}

.multiselect-custom :deep(.multiselect--active .multiselect__select:before) {
  border-color: var(--primary-color) transparent transparent transparent !important;
}

/* Placeholder */
.multiselect-custom :deep(.multiselect__placeholder) {
  color: var(--text-muted) !important;
  font-size: 14px !important;
  padding-top: 0 !important;
  margin-bottom: 0 !important;
  line-height: 1.5 !important;
}

/* Container do dropdown */
.multiselect-custom :deep(.multiselect__content-wrapper) {
  background: var(--card-background) !important;
  border: 2px solid var(--border-color) !important;
  border-top: none !important;
  border-radius: 0 0 8px 8px !important;
  box-shadow: var(--shadow-md) !important;
  max-height: 200px !important;
  z-index: 99999999 !important;
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
}

/* Opções do dropdown */
.multiselect-custom :deep(.multiselect__option) {
  padding: 12px 16px !important;
  color: var(--text-primary) !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
  border-bottom: 1px solid var(--bg-tertiary) !important;
  transition: var(--transition) !important;
}

.multiselect-custom :deep(.multiselect__option:last-child) {
  border-bottom: none !important;
}

/* Opção em hover */
.multiselect-custom :deep(.multiselect__option--highlight) {
  background: var(--primary-color) !important;
  color: white !important;
}

.multiselect-custom :deep(.multiselect__option--highlight:after) {
  background: var(--primary-color) !important;
  color: white !important;
}

/* Opção selecionada */
.multiselect-custom :deep(.multiselect__option--selected) {
  background: var(--primary-color-10) !important;
  color: var(--primary-color) !important;
  font-weight: 600 !important;
}

.multiselect-custom :deep(.multiselect__option--selected:after) {
  color: var(--primary-color) !important;
  background: transparent !important;
}

/* Estado desabilitado */
.multiselect-custom :deep(.multiselect--disabled) {
  background: var(--bg-tertiary) !important;
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

/* Scrollbar personalizada para o dropdown */
.multiselect-custom :deep(.multiselect__content-wrapper::-webkit-scrollbar) {
  width: 6px !important;
}

.multiselect-custom :deep(.multiselect__content-wrapper::-webkit-scrollbar-track) {
  background: var(--bg-tertiary) !important;
  border-radius: 3px !important;
}

.multiselect-custom :deep(.multiselect__content-wrapper::-webkit-scrollbar-thumb) {
  background: var(--border-color) !important;
  border-radius: 3px !important;
}

.multiselect-custom :deep(.multiselect__content-wrapper::-webkit-scrollbar-thumb:hover) {
  background: var(--text-secondary) !important;
}

/* Responsividade */
@media (max-width: 768px) {
  .multiselect-custom :deep(.multiselect) {
    min-height: 40px !important;
  }
  
  .multiselect-custom :deep(.multiselect__tags) {
    min-height: 40px !important;
    padding: 6px 36px 6px 10px !important;
  }
  
  .multiselect-custom :deep(.multiselect__tag) {
    font-size: 11px !important;
    padding: 3px 22px 3px 8px !important;
    margin-right: 6px !important;
  }
  
  .multiselect-custom :deep(.multiselect__select) {
    height: 38px !important;
    width: 36px !important;
  }
  
  .multiselect-custom :deep(.multiselect__option) {
    padding: 10px 12px !important;
    font-size: 13px !important;
  }
}

/* Animações suaves */
.multiselect-custom :deep(.multiselect__content-wrapper) {
  animation: slideDown 0.2s ease-out !important;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Melhorias visuais para o tema escuro */
[data-theme="dark"] .multiselect-custom :deep(.multiselect__option) {
  border-bottom-color: var(--bg-secondary) !important;
}

[data-theme="dark"] .multiselect-custom :deep(.multiselect__content-wrapper::-webkit-scrollbar-track) {
  background: var(--bg-secondary) !important;
}

[data-theme="dark"] .multiselect-custom :deep(.multiselect__content-wrapper::-webkit-scrollbar-thumb) {
  background: var(--accent-primary) !important;
}

[data-theme="dark"] .multiselect-custom :deep(.multiselect__content-wrapper::-webkit-scrollbar-thumb:hover) {
  background: var(--text-secondary) !important;
}
</style>